import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import { Card, CardContent, CardHeader } from "@/components/atomic/Card";
import { cn } from "@/lib/utils";
import { ChangeType } from "@/models/vocCount";

interface Props {
  title?: string;
  totalCount: number;
  changeAmount: number;
  changeType: ChangeType;
  previousPeriodComparison?: number; // 前期比 (percentage value, e.g., 10 for +10%, -10 for -10%)
  comparisonLabelKey: string; // Translation key for the comparison label (e.g., "bi.chart.yearlyComparison" or "bi.chart.monthlyComparison")
  className?: string;
}

const ComparisonChart: React.FC<Props> = ({
  title,
  totalCount,
  changeAmount,
  previousPeriodComparison,
  changeType,
  comparisonLabelKey,
  className,
}) => {
  const { t } = useTranslation("voiceek_ui");

  const absComparison = Math.abs(previousPeriodComparison ?? 0);

  const isPositive = changeType === "positive";
  const isUnknown = changeType === "unknown";

  const compValue = match(changeType)
    .with("positive", () => `+${absComparison}`)
    .with("negative", () => `-${absComparison}`)
    .otherwise(() => "-");

  // Calculate the stroke-dasharray and stroke-dashoffset for the circular progress
  const radius = 60; // Radius of the circle
  const strokeWidth = 15;
  const normalizedRadius = radius - strokeWidth / 2;
  const circumference = 2 * Math.PI * normalizedRadius;
  const progressPercentage = 76; // The circle is incomplete, showing only 76% of the full circle
  const maxProgress = (progressPercentage / 100) * circumference;
  const dashOffset = isUnknown ? 0 : maxProgress - (maxProgress / 100 * Math.min(absComparison, 100));

  return (
    <div className={cn("bg-white flex flex-row box-content", className)}>
      <Card className="w-[300px] h-[180px] relative border-0 shadow-none">
        <CardHeader className="px-4 pt-4 pb-3 text-[13px] font-bold leading-[1.6]">
          {title}
        </CardHeader>
        <CardContent className="p-0">
          <div className="flex justify-center px-5 items-center">
            {/* Left side - Circular progress indicator */}
            <div className="relative w-[120px] h-[120px] mr-6">
              <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="circleGradientPositive" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#4BDFDB" />
                    <stop offset="100%" stopColor="#5871FE" />
                  </linearGradient>
                  <linearGradient id="circleGradientNegative" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#9B58E9" />
                    <stop offset="100%" stopColor="#FE5757" />
                  </linearGradient>
                  <linearGradient id="circleBackground" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#E8EDFB" />
                    <stop offset="100%" stopColor="#E8EDFB" />
                  </linearGradient>
                </defs>
                <circle
                  cx="60"
                  cy="60"
                  r={normalizedRadius}
                  stroke="url(#circleBackground)"
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                  strokeDasharray={`${maxProgress} ${circumference}`}
                  transform="rotate(133.2 60 60)"
                />
                {changeType !== "unknown" && (
                  <circle
                    cx="60"
                    cy="60"
                    r={normalizedRadius}
                    stroke={isPositive ? "url(#circleGradientPositive)" : "url(#circleGradientNegative)"}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={`${maxProgress} ${circumference}`}
                    strokeDashoffset={dashOffset}
                    transform="rotate(133.2 60 60)"
                  />
                )}
                <text
                  x="60"
                  y="66"
                  dominantBaseline="alphabetic"
                  textAnchor="middle"
                  className={cn(
                    "font-bold text-[24px] fill-[var(--compchart-negative)]",
                    isPositive && "fill-[var(--compchart-positive)]",
                    isUnknown && "fill-[#E8EDFB]",
                  )}
                >
                  {compValue}
                  {changeType !== "unknown" && <tspan className="text-[#333333] text-xs">%</tspan>}
                </text>
                <text
                  x="60"
                  y="98"
                  dominantBaseline="middle"
                  textAnchor="middle"
                  className="font-normal fill-[#333333] text-[10px]"
                >
                  {t(comparisonLabelKey)}
                </text>
              </svg>
            </div>

            {/* Right side - Statistics */}
            <div className="flex flex-col gap-[2px] mt-[-10px]">
              {/* Total row */}
              <div className="flex items-center text-[#333333]">
                <div className="w-[32px] font-normal text-xs">{t("bi.chart.total")}</div>
                <div className="font-normal text-xs">：</div>
                <div className="font-normal text-xs">
                  <span className="font-normal text-xs">{totalCount.toLocaleString()}</span>
                  <span className="text-[10px] pl-[2px]">{t("units.cases")}</span>
                </div>
              </div>

              {/* Change row */}
              <div className="flex items-center mt-[3px] text-[#333333]">
                <div className="w-[32px] font-normal text-xs">{t("bi.chart.change")}</div>
                <div className="font-normal text-xs">：</div>
                <div className="font-normal text-xs">
                  {isPositive && (
                    <span
                      className={cn(
                        "font-bold text-[var(--compchart-negative)]",
                        isPositive && "text-[var(--compchart-positive)]",
                      )}
                    >
                      +
                    </span>
                  )}
                  <span
                    className={cn(
                      "font-bold text-[var(--compchart-negative)]",
                      isPositive && "text-[var(--compchart-positive)]",
                      isUnknown && "font-normal text-[#333333]",
                    )}
                  >
                    {isUnknown ? "-" : changeAmount.toLocaleString()}
                  </span>
                  <span className="text-[10px] pl-[2px]">{t("units.cases")}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ComparisonChart;
